import { useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function VideoCarousel({
  id,
  sectionTestId,
  testIdPrefix,
  eyebrow,
  headingPrimary,
  headingItalic,
  description,
  videos,
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", containScroll: false, dragFree: true },
    [AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false, stopOnMouseEnter: true, stopOnFocusIn: true })]
  );
  const [active, setActive] = useState(null);
  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <section id={id} data-testid={sectionTestId} className="border-b border-white/10 py-10 md:py-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-5 md:mb-8">
          <div>
            <h2 className="mt-3 font-oswald uppercase text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.95]">
              {headingPrimary}{" "}
              <span className="font-cormorant italic font-medium normal-case lowercase text-white/70">{headingItalic}</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base">{description}</p>
        </div>

        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 md:gap-6">
              {videos.map((v, i) => (
                <button
                  key={v.id}
                  data-testid={`${testIdPrefix}-slide-${i}`}
                  onClick={() => setActive(v)}
                  className="group relative shrink-0 basis-[85%] sm:basis-[60%] md:basis-[48%] lg:basis-[38%] aspect-video overflow-hidden border border-white/10 hover:border-[#00A0E4]/50 bg-[#070707] transition-colors text-left"
                >
                  <img
                    src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                    onError={(e) => {
                      e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`;
                    }}
                    alt={v.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[900ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:bg-[#FF2A2A] group-hover:border-[#FF2A2A] transition-colors">
                      <Play className="text-white ml-0.5" size={20} fill="currentColor" />
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                    {v.client ? (
                      <p className="font-oswald uppercase text-[10px] tracking-widest text-[#00A0E4]">{v.client}</p>
                    ) : null}
                    <p className="font-cormorant italic text-base md:text-lg text-white line-clamp-1">{v.title}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4">
            <p className="font-oswald uppercase text-xs tracking-widest text-white/40">
              Scrolling · Hover to pause
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={scrollPrev}
                data-testid={`${testIdPrefix}-prev`}
                aria-label="Previous"
                className="w-11 h-11 flex items-center justify-center border border-white/20 hover:border-[#00A0E4] hover:text-[#00A0E4] text-white transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={scrollNext}
                data-testid={`${testIdPrefix}-next`}
                aria-label="Next"
                className="w-11 h-11 flex items-center justify-center border border-white/20 hover:border-[#00A0E4] hover:text-[#00A0E4] text-white transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid={`${testIdPrefix}-lightbox`}
          className="max-w-5xl w-[95vw] p-0 bg-[#000000] border-white/10 rounded-none"
        >
          <DialogTitle className="sr-only">{active?.title || "Reel"}</DialogTitle>
          <DialogDescription className="sr-only">
            {active ? `${active.client || ""} — ${active.title}. YouTube video player.` : "Video player"}
          </DialogDescription>
          {active && (
            <div className="relative">
              <button
                onClick={() => setActive(null)}
                data-testid={`${testIdPrefix}-lightbox-close`}
                className="absolute -top-10 right-0 text-white/80 hover:text-[#00A0E4] flex items-center gap-2 font-oswald uppercase text-xs tracking-widest"
              >
                Close <X size={16} />
              </button>
              <div className="aspect-video w-full bg-black">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${active.id}?autoplay=1&rel=0`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-5 md:p-6 border-t border-white/10 flex items-center justify-between">
                <div>
                  {active.client ? (
                    <p className="font-oswald uppercase text-[10px] tracking-widest text-[#00A0E4]">{active.client}</p>
                  ) : null}
                  <p className="font-cormorant italic text-xl text-white">{active.title}</p>
                </div>
                <a
                  href={`https://www.youtube.com/watch?v=${active.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost text-xs"
                >
                  Open on YouTube
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
